import React from "react";
import PropTypes from "prop-types";
import BackToMuzoneButton from "../../molecules/BackToMuzoneButton";
import "./LegalPageLayout.scss";

/**
 * Shell for the public legal documents (privacy policy, third-party
 * attribution). These pages are reached from the signed-out auth footer, so
 * they carry the editorial identity rather than the generic navy they used to.
 *
 * The layout owns the scroll container so the return bar can stick to the top
 * of it — the app locks `body` overflow, so each of these pages has to scroll
 * itself.
 */
const LegalPageLayout = ({
    className = "",
    eyebrow,
    title,
    titleId,
    meta,
    lead,
    backTo = "/login",
    children,
}) => {
    const composedClassName = ["legal-page", className].filter(Boolean).join(" ");

    return (
        <div className={composedClassName}>
            <div className="legal-page__bloom" aria-hidden="true" />

            {/* Always-reachable exit. Sticky rather than fixed so it cannot
                cover content on short viewports or during rubber-band scroll. */}
            <div className="legal-page__bar">
                <div className="legal-page__bar-inner">
                    <BackToMuzoneButton to={backTo} className="back-to-muzone--compact" />
                    {title ? (
                        <span className="legal-page__bar-title" aria-hidden="true">
                            {title}
                        </span>
                    ) : null}
                </div>
            </div>

            <main className="legal-page__main">
                <article className="legal-page__card" aria-labelledby={titleId}>
                    <header className="legal-page__header">
                        {eyebrow ? <p className="legal-page__eyebrow">{eyebrow}</p> : null}
                        <h1 className="legal-page__title" id={titleId}>
                            {title}
                        </h1>
                        {meta ? <p className="legal-page__meta">{meta}</p> : null}
                        {lead ? <p className="legal-page__lead">{lead}</p> : null}
                    </header>

                    <div className="legal-page__body">{children}</div>
                </article>
            </main>
        </div>
    );
};

LegalPageLayout.propTypes = {
    className: PropTypes.string,
    eyebrow: PropTypes.node,
    title: PropTypes.node.isRequired,
    titleId: PropTypes.string,
    meta: PropTypes.node,
    lead: PropTypes.node,
    backTo: PropTypes.string,
    children: PropTypes.node,
};

export default LegalPageLayout;
